import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { apis } from '../../services/Api';
import styles from './chatbot.module.scss';
import ChatHeader from './ChatHeader';
import ChatSidebar from './ChatSidebar';
import ChatMessages from './ChatMessages';
import ChatConversationModal from './ChatConversationModal';

const Chatbot = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [conversations, setConversations] = useState([]);
    const [inputText, setInputText] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [currentConversationId, setCurrentConversationId] = useState(null);
    const [activeMenuId, setActiveMenuId] = useState(null);
    const [modalConfig, setModalConfig] = useState({ isOpen: false, type: '', chat: null });
    const [renameInput, setRenameInput] = useState('');
    const [userData, setUserData] = useState(null);
    const [syncing, setSyncing] = useState(false);
    const [syncSeconds, setSyncSeconds] = useState(0);
    const [syncNotice, setSyncNotice] = useState(null);
    const history = useHistory();
    const messagesEndRef = useRef(null);
    const abortControllerRef = useRef(null);

    const goToIdeas = (cid) => {
        setIsOpen(false);
        console.log(cid);
        // history.push({
        // pathname: `https://app-ipportal.harbingergroup.com/allips/idea/${cid}`
        //     pathname: `/allips/idea/${cid}`
        // });
        window.location.href = `https://app-ipportal.harbingergroup.com/allips/idea/${cid}`;
    }


    // Fetch user info and conversations independently on mount
    const fetchData = async () => {
        try {
            const convs = await apis.getChatConversations();
            setConversations(convs);
        } catch (error) {
            console.error('Error fetching conversations:', error);
        }

        try {
            const user = await apis.getUserDetails();
            setUserData(user);
        } catch (error) {
            console.error('Error fetching user data:', error);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        const unsubscribe = apis.subscribeChatCredits(
            (credits) => {
                setUserData((prev) => ({ ...(prev || {}), available_credits: credits }));
            },
            (error) => {
                console.error('Credits stream error:', error);
            }
        );

        return () => {
            if (unsubscribe) {
                unsubscribe();
            }
        };
    }, []);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isLoading]);

    useEffect(() => {
        if (!syncing) return;
        const timer = window.setInterval(() => setSyncSeconds(value => value + 1), 1000);
        return () => window.clearInterval(timer);
    }, [syncing]);

    const toggleChat = () => setIsOpen(!isOpen);
    const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

    useEffect(() => {
        const handleClickOutside = () => {
            setActiveMenuId(null);
        };
        window.addEventListener('click', handleClickOutside);
        return () => window.removeEventListener('click', handleClickOutside);
    }, []);

    const handleNewChat = () => {
        setMessages([]);
        setCurrentConversationId(null);
    };

    const openRenameModal = (chat, e) => {
        e.stopPropagation();
        setActiveMenuId(null);
        setRenameInput(chat.title || `Chat ${chat.id}`);
        setModalConfig({ isOpen: true, type: 'rename', chat });
    };

    const openDeleteModal = (chat, e) => {
        e.stopPropagation();
        setActiveMenuId(null);
        setModalConfig({ isOpen: true, type: 'delete', chat });
    };

    const closeModal = () => {
        setModalConfig({ isOpen: false, type: '', chat: null });
        setRenameInput('');
    };

    const handleRenameConfirm = async () => {
        const { chat } = modalConfig;
        if (renameInput && renameInput.trim() && chat) {
            try {
                await apis.renameChatConversation(chat.id, renameInput.trim());
                fetchData();
            } catch (error) {
                console.error("Failed to rename conversation:", error);
                alert("Failed to rename conversation.");
            }
        }
        closeModal();
    };

    const handleDeleteConfirm = async () => {
        const { chat } = modalConfig;
        if (chat) {
            try {
                await apis.deleteChatConversation(chat.id);
                if (currentConversationId === chat.id) {
                    setMessages([]);
                    setCurrentConversationId(null);
                }
                fetchData();
            } catch (error) {
                console.error("Failed to delete conversation:", error);
                alert("Failed to delete conversation.");
            }
        }
        closeModal();
    };

    const loadChat = async (chat) => {
        setIsLoading(true);
        try {
            const data = await apis.getChatConversationDetails(chat.id);
            setMessages(data.messages || []);
            setCurrentConversationId(chat.id);
        } catch (error) {
            console.error('Error loading conversation:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const stopGenerating = () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            setIsLoading(false);
            abortControllerRef.current = null;
        }
    };

    const formatTime = (timestamp) => {
        if (!timestamp) return '';
        return new window.Intl.DateTimeFormat('en-US', {
            hour: 'numeric',
            minute: 'numeric',
            hour12: true
        }).format(new Date(timestamp));
    };

    const handleSend = async () => {
        if (!inputText.trim() || isLoading) return;

        const textToSend = inputText;
        const userMsg = {
            id: `temp-${Date.now()}`,
            role: 'user',
            content: textToSend,
            timestamp: Date.now()
        };

        setMessages((prev) => [...prev, userMsg]);
        setInputText('');
        setIsLoading(true);

        abortControllerRef.current = new window.AbortController();

        try {
            const data = await apis.sendChatMessage(
                textToSend,
                currentConversationId,
                abortControllerRef.current.signal
            );

            // Fetch new history list if this was a new chat
            if (!currentConversationId) {
                setCurrentConversationId(data.conversation_id);
                fetchData();
            }

            setMessages((prev) => {
                const newMessages = [...prev];
                let lastUserMsgIndex = -1;
                for (let i = newMessages.length - 1; i >= 0; i--) {
                    if (newMessages[i].role === 'user') {
                        lastUserMsgIndex = i;
                        break;
                    }
                }

                if (lastUserMsgIndex !== -1) {
                    newMessages[lastUserMsgIndex] = {
                        ...newMessages[lastUserMsgIndex],
                        id: data.user_message_id
                    };
                }

                // Add AI response
                const aiReply = {
                    id: data.message_id,
                    role: 'assistant',
                    content: data.response,
                    timestamp: Date.now(),
                    sources: data.sources
                };
                return [...newMessages, aiReply];
            });
        } catch (error) {
            console.error('Error sending message:', error);
            const errorReply = {
                id: `error-${Date.now()}`,
                role: 'assistant',
                content: error.response?.data?.detail || 'Sorry, I encountered an error. Please try again later.',
                timestamp: Date.now(),
            };
            setMessages((prev) => [...prev, errorReply]);
        } finally {
            setIsLoading(false);
            abortControllerRef.current = null;
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSend();
        }
    };

    const handleFeedback = async (messageId, feedback) => {
        try {
            await apis.sendChatFeedback(messageId, feedback);
            setMessages(prev => prev.map(m => m.id === messageId ? { ...m, feedback } : m));
        } catch (error) {
            console.error('Error sending feedback:', error);
        }
    };

    const syncDocuments = async () => {
        setSyncSeconds(0);
        setSyncNotice({
            variant: 'info',
            title: 'Sync in progress',
            message: 'Downloading, extracting, and embedding changed files.',
            failures: []
        });
        setSyncing(true);
        try {
            const result = await apis.syncSharePointCollateral();
            const failures = result.failed?.length
                ? result.failed
                : result.review_required?.flatMap(item =>
                    item.warnings.map(warning => `${item.name} — ${warning.field}: ${warning.message}`)
                ) || [];
            setSyncNotice({
                variant: failures.length ? 'warning' : 'success',
                title: failures.length ? 'Sync completed with review items' : 'Documents synced',
                message: result.discovered
                    ? `${result.changed} updated, ${result.unchanged} unchanged, ${result.skipped.length} unsupported.`
                    : 'The knowledge base is up to date.',
                failures
            });
        } catch (error) {
            setSyncNotice({
                variant: 'danger',
                title: 'Sync failed',
                message: error.response?.data?.detail || 'SharePoint document sync failed.',
                failures: []
            });
        } finally {
            setSyncing(false);
        }
    };

    return (
        <div className={styles.chatbotContainer}>
            {isOpen && (
                <div className={styles.chatWindow}>
                    <ChatHeader
                        isLoading={isLoading}
                        onStopGenerating={stopGenerating}
                        onClose={toggleChat}
                    />
                    <div className={styles.layout}>
                        <ChatSidebar
                            isSidebarOpen={isSidebarOpen}
                            onToggleSidebar={toggleSidebar}
                            onNewChat={handleNewChat}
                            conversations={conversations}
                            currentConversationId={currentConversationId}
                            onLoadChat={loadChat}
                            activeMenuId={activeMenuId}
                            onToggleMenu={(chatId) => setActiveMenuId(activeMenuId === chatId ? null : chatId)}
                            onOpenRenameModal={openRenameModal}
                            onOpenDeleteModal={openDeleteModal}
                            userData={userData}
                            syncing={syncing}
                            syncSeconds={syncSeconds}
                            syncNotice={syncNotice}
                            onSyncDocuments={syncDocuments}
                            onGoToDashboard={() => {
                                setIsOpen(false);
                                history.push('/');
                            }}
                        />

                        <div className={`${styles.mainArea} ${!isSidebarOpen ? styles.fullWidth : ''}`}>
                            <ChatMessages
                                messages={messages}
                                isLoading={isLoading}
                                formatTime={formatTime}
                                messagesEndRef={messagesEndRef}
                                onGoToIdeas={goToIdeas}
                                onFeedback={handleFeedback}
                            />

                            <div className={styles.inputArea}>
                                <input
                                    type="text"
                                    value={inputText}
                                    onChange={(e) => setInputText(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    placeholder="Type a message..."
                                    className={styles.inputField}
                                    disabled={isLoading}
                                />
                                <button onClick={handleSend} className={styles.sendBtn} disabled={isLoading}>
                                    <i className="bi bi-send-fill"></i>
                                </button>
                            </div>
                        </div>
                    </div>

                    <ChatConversationModal
                        modalConfig={modalConfig}
                        renameInput={renameInput}
                        onRenameInputChange={setRenameInput}
                        onClose={closeModal}
                        onRenameConfirm={handleRenameConfirm}
                        onDeleteConfirm={handleDeleteConfirm}
                    />
                </div>
            )}

            {!isOpen && (
                <button className={styles.fab} onClick={toggleChat}>
                    <i className="bi bi-chat-dots-fill"></i>
                </button>
            )}
        </div>
    );
};

export default Chatbot;
